import type { JSX } from "react";
import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Loader2 } from "lucide-react";
import type { TaxonomyKind, TaxonomyApiItem } from "@recipevault/shared";
import { useTaxonomy } from "./useTaxonomy.js";
import { deleteTaxonomy } from "./api.js";
import { TaxonomyFormDialog } from "./TaxonomyFormDialog.js";
import { KIND_LABEL, KIND_LABEL_SINGULAR } from "./kindLabels.js";
import { friendlyError } from "../../shared/http.js";
import { db } from "../../sync/db.js";
import { Button } from "@/ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog.js";

const KIND_DESCRIPTION: Record<TaxonomyKind, string> = {
  tag: "Free-form labels for sorting and filtering your recipes.",
  category: "High-level groupings like Breakfast, Dinner, Dessert.",
  cuisine: "Cuisines like Italian, Thai, Mexican.",
  diet: "Dietary flags like Vegan, Gluten-free, Kosher.",
};

interface Props {
  kind: TaxonomyKind;
  isOwner: boolean;
}

export function TaxonomyList({ kind, isOwner }: Props): JSX.Element {
  const { items, status } = useTaxonomy(kind);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TaxonomyApiItem | null>(null);
  const [deleting, setDeleting] = useState<TaxonomyApiItem | null>(null);
  const [busy, setBusy] = useState(false);

  async function confirmDelete(): Promise<void> {
    if (!deleting) return;
    setBusy(true);
    try {
      await deleteTaxonomy(kind, deleting.id);
      // Write-through Dexie so the live query removes the row before the next sync.
      await db.taxonomy.update(deleting.id, {
        deletedAt: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
      });
      toast.success(`Deleted ${KIND_LABEL_SINGULAR[kind]} '${deleting.name}'`);
      setDeleting(null);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-zinc-50 font-medium">{KIND_LABEL[kind]}</h2>
          <p className="text-zinc-500 text-xs mt-1">{KIND_DESCRIPTION[kind]}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="sm" className="self-start sm:self-auto">
          <Plus className="mr-2 h-4 w-4" />
          New {KIND_LABEL_SINGULAR[kind]}
        </Button>
      </div>

      {status === "loading" && (
        <div className="flex items-center text-zinc-500 text-sm py-2">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}
      {status === "ready" && items.length === 0 && (
        <p className="text-zinc-500 text-sm py-4">
          No {KIND_LABEL[kind].toLowerCase()} yet.
        </p>
      )}
      {status === "ready" && items.length > 0 && (
        <ul>
          {items.map((i) => (
            <li
              key={i.id}
              className="flex items-center gap-3 py-2.5 border-b border-zinc-800 last:border-b-0"
            >
              {kind === "tag" && (
                <span
                  className="w-4 h-4 rounded-full border border-zinc-700 shrink-0"
                  style={{ background: i.color ?? "var(--primary)" }}
                  aria-label={i.color ?? "default color"}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-zinc-100 text-sm font-medium truncate">{i.name}</div>
                {kind === "tag" && i.color && (
                  <div className="text-zinc-500 text-xs truncate">{i.color}</div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(i)}
                aria-label={`Edit ${i.name}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              {isOwner && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDeleting(i)}
                  aria-label={`Delete ${i.name}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <TaxonomyFormDialog
        kind={kind}
        mode="create"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          toast.success(`Created ${KIND_LABEL_SINGULAR[kind]}`);
        }}
      />
      {editing && (
        <TaxonomyFormDialog
          kind={kind}
          mode="edit"
          open
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast.success(`Updated ${KIND_LABEL_SINGULAR[kind]}`);
          }}
        />
      )}
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {KIND_LABEL_SINGULAR[kind]}?</DialogTitle>
            <DialogDescription>
              &ldquo;{deleting?.name}&rdquo; will be soft-deleted and removed permanently after 30 days. Recipes
              referencing it will keep the reference until you edit them.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => { void confirmDelete(); }}
              disabled={busy}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
